import React, { useCallback, useEffect, useMemo, useState } from "react";
import SearchFilter from "../../../../../ui/SearchFilter/SearchFilter";
import Filters from "../../../../../ui/Filters/Filters";
import useSurveysByStatus from "../../hooks/useSurveysByStatus";
import { httpService } from "../../../../../base/services/httpService.service";
import { LocalStorageService } from "../../../../../base/services/local-storage.service";
import {
   collectionTypeEnumeratorsOptions,
   collectionTypeMessagesOptions,
   genderOptions,
   taskStatusOptions
} from "../../../../../utils/options";
import useGetMessagingTaskReceivers from "../../hooks/useGetMessagingTaskReceivers";

const TaskManagementFilters = React.memo(
   ({
      enumeratorsPage = false,
      onSelectChange,
      filterValue,
      collectionMessagesPage = false,
      surveysTab = false,
      handleSearch,
      handleClearFilters
   }) => {
      const workspaceId = LocalStorageService.getItem("currentWorkspaceId") || "";
      const projectID = LocalStorageService.getItem("currentProjectID");

      const [enumerators, setEnumerators] = useState([]);
      const [locationGroups, setLocationGroups] = useState([]);
      const { data: surveys = [] } = useSurveysByStatus(enumeratorsPage);
      const { data: beneficiaries = [] } = useGetMessagingTaskReceivers(collectionMessagesPage);

      const fetchAll = useCallback(() => {
         if (collectionMessagesPage) return;

         if (!enumeratorsPage) {
            const params = new URLSearchParams({ ProjectId: projectID, isDeleted: false });
            httpService.get(
               `/Task/getEnumeratorNamesByProject?${params.toString()}`,
               (response) => {
                  if (response?.data) setEnumerators(response.data);
               }
            );
         }

         const url = enumeratorsPage
            ? `/Task/getDataCollectorTasksLocations?isDeleted=false`
            : `/Task/GetLocationGroups?workspaceId=${workspaceId}`;

         httpService.get(url, (response) => {
            if (!response?.data) return;
            setLocationGroups(enumeratorsPage ? response.data : response.data.locationGroups);
         });
      }, [collectionMessagesPage, enumeratorsPage, projectID, workspaceId]);

      useEffect(() => {
         fetchAll();
      }, [fetchAll]);

      const filterConfigs = useMemo(
         () => [
            ...(!enumeratorsPage && !collectionMessagesPage
               ? [
                    {
                       key: "dataCollectors",
                       label: "Data Collector name",
                       placeholder: "Select an option",
                       options: enumerators.map((item) => ({ value: item, label: item }))
                    },
                    ...(!surveysTab
                       ? [
                            {
                               key: "gender",
                               label: "Data Collector gender",
                               placeholder: "Select an option",
                               options: genderOptions
                            }
                         ]
                       : [])
                 ]
               : []),
            ...(!collectionMessagesPage
               ? [
                    {
                       key: "locationGroupId",
                       label: "Location",
                       placeholder: "Select an option",
                       options: locationGroups.map((item) => ({ value: item.id, label: item.name }))
                    }
                 ]
               : []),
            ...(!surveysTab
               ? [
                    {
                       key: collectionMessagesPage ? "surveyName" : "surveyId",
                       label: "Survey tool",
                       placeholder: "Select a survey tool",
                       options: surveys.map((item) => ({
                          value: collectionMessagesPage ? item.name : item.id,
                          label: item.name
                       }))
                    },
                    {
                       key: "collectionType",
                       label: "Collection type",
                       placeholder: "Select an option",
                       options: collectionMessagesPage
                          ? collectionTypeMessagesOptions
                          : collectionTypeEnumeratorsOptions
                    },
                    {
                       key: collectionMessagesPage ? "sendingDate" : "tasksDeadline",
                       label: collectionMessagesPage ? "Sending date" : "Deadline",
                       type: "calendar"
                    },
                    ...(!collectionMessagesPage
                       ? [
                            {
                               key: "taskStatus",
                               label: "Status",
                               placeholder: "Select status",
                               options: taskStatusOptions
                            }
                         ]
                       : []),
                    ...(collectionMessagesPage
                       ? [
                            {
                               key: "receiver",
                               label: "Receiver",
                               placeholder: "Select receiver",
                               options: beneficiaries.map((item) => ({ value: item, label: item }))
                            }
                         ]
                       : [])
                 ]
               : [])
         ],
         [
            enumerators,
            locationGroups,
            surveys,
            beneficiaries,
            collectionMessagesPage,
            enumeratorsPage,
            surveysTab
         ]
      );

      return (
         <SearchFilter handleSearch={handleSearch} handleClearFilters={handleClearFilters}>
            <Filters
               onSelectChange={onSelectChange}
               filterValue={filterValue}
               filterConfigs={filterConfigs}
            />
         </SearchFilter>
      );
   }
);

TaskManagementFilters.displayName = "TaskManagementFilters";

export default TaskManagementFilters;
